import React, { useState } from 'react';
import {
    Box, Chip, Drawer, Typography, List, ListItemButton, ListItemText, TextField, InputAdornment, IconButton,
} from '@mui/material';
import { ExpandMore, Check, Close, Search as SearchIcon } from '@mui/icons-material';
import { TOUCH } from '../../theme/tokens';
import ScrollChipRow from './ScrollChipRow';

const FRONT = 2;   // areas shown as chips before "+N more"

/**
 * Area filter on ONE line, however many areas the vendor has.
 *
 * With 20+ areas the old chips wrapped onto seven rows and pushed the list off the screen. Now:
 *   ( All areas ) ( ✓ chosen ) ( busiest ) ( next busiest ) ( +18 more ▾ )
 * The two areas with the most customers stay one tap away; the rest, and "No area", are in a
 * list that slides up from the bottom. Whatever is chosen moves to the front, so what is applied
 * is always on screen. If a phone is too narrow even for this, the line scrolls sideways rather
 * than wrapping.
 *
 * options   [{ key, label, count }] in route order, areas with nobody in them left out
 * noArea    { key, count } or null — customers with no area
 * total     count for "All areas"
 * value     chosen key, or '' for all
 * onChange  (key) => void; '' clears
 */
export default function AreaFilterRow({ options, noArea, total, value, onChange, sx }) {
    const [open, setOpen] = useState(false);
    const [search, setSearch] = useState('');

    const all = [...options, ...(noArea && noArea.count > 0 ? [{ ...noArea, label: 'No area' }] : [])];
    // Nothing to choose between with a single bucket.
    if (all.length < 2) return null;

    const chosen = all.find(o => o.key === value) || null;
    // Busiest first; route order breaks ties so the pick is stable.
    const front = options
        .map((o, i) => ({ ...o, i }))
        .filter(o => o.key !== value)
        .sort((a, b) => b.count - a.count || a.i - b.i)
        .slice(0, FRONT);
    const hidden = all.length - front.length - (chosen ? 1 : 0);

    const close = () => { setOpen(false); setSearch(''); };
    const pick = (key) => { onChange(key === value ? '' : key); close(); };

    const q = search.trim().toLowerCase();
    // The sheet lists areas A-Z (numbers in order: "Hiland 2" before "Hiland 10"), with
    // "No area" kept last. Scanning a long list for a name is quicker alphabetically than in
    // route order.
    const byName = (a, b) => a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });
    const listed = [...options].sort(byName)
        .concat(all.length > options.length ? [all[all.length - 1]] : [])
        .filter(o => !q || o.label.toLowerCase().includes(q));

    const chip = (o, active) => (
        <Chip key={o.key} label={`${o.label} (${o.count})`} size="small" clickable
            icon={active ? <Check /> : undefined}
            color={active ? 'secondary' : 'default'}
            variant={active ? 'filled' : 'outlined'}
            onClick={() => pick(o.key)}
            sx={{ flexShrink: 0, maxWidth: 170 }} />
    );

    return (
        <>
            <ScrollChipRow sx={sx}>
                <Chip label={`All areas (${total})`} size="small" clickable
                    color={!value ? 'primary' : 'default'} variant={!value ? 'filled' : 'outlined'}
                    onClick={() => onChange('')} sx={{ flexShrink: 0 }} />
                {chosen && chip(chosen, true)}
                {front.map(o => chip(o, false))}
                {hidden > 0 && (
                    <Chip label={`+${hidden} more`} size="small" clickable variant="outlined" color="primary"
                        deleteIcon={<ExpandMore />} onDelete={() => setOpen(true)}
                        onClick={() => setOpen(true)} sx={{ flexShrink: 0, fontWeight: 700 }} />
                )}
            </ScrollChipRow>

            {/* Every area, A-Z, with "No area" last */}
            {/* zIndex 1400: above the app's bottom navigation bar (1300), which otherwise sat on
                top of the sheet and hid the last areas and "No area". MUI's default for a bottom
                sheet is 1200. Same value the order page's address sheet uses. */}
            <Drawer anchor="bottom" open={open} onClose={close} sx={{ zIndex: 1400 }}
                PaperProps={{ sx: {
                    borderTopLeftRadius: 16, borderTopRightRadius: 16, maxHeight: '75vh',
                    maxWidth: 720, mx: 'auto', pb: 'env(safe-area-inset-bottom, 0px)',
                } }}>
                <Box sx={{ px: 2, pt: 2, pb: 1 }}>
                    {/* ✕ for a wrong tap on "+N more": back to the list with nothing changed */}
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="subtitle1" fontWeight={800}>Choose area</Typography>
                        <IconButton onClick={close} aria-label="Close" edge="end" sx={{ width: 44, height: 44 }}>
                            <Close />
                        </IconButton>
                    </Box>
                    {all.length > 8 && (
                        <TextField size="small" fullWidth placeholder="Search area" value={search}
                            onChange={e => setSearch(e.target.value)} sx={{ mt: 1 }}
                            InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }} />
                    )}
                </Box>
                <List dense sx={{ overflowY: 'auto', pt: 0 }}>
                    {!q && (
                        <ListItemButton selected={!value} onClick={() => { onChange(''); setOpen(false); }} sx={{ minHeight: TOUCH.min }}>
                            <ListItemText primary="All areas" primaryTypographyProps={{ fontWeight: 600 }} />
                            <Typography variant="body2" color="text.secondary">{total}</Typography>
                        </ListItemButton>
                    )}
                    {listed.map(o => (
                        <ListItemButton key={o.key} selected={o.key === value} onClick={() => pick(o.key)}
                            sx={{ minHeight: TOUCH.min }}>
                            <ListItemText primary={o.label}
                                primaryTypographyProps={{ fontWeight: o.key === value ? 700 : 500,
                                    color: o.key === noArea?.key ? 'text.secondary' : 'text.primary' }} />
                            {o.key === value && <Check fontSize="small" color="secondary" sx={{ mr: 1 }} />}
                            <Typography variant="body2" color="text.secondary">{o.count}</Typography>
                        </ListItemButton>
                    ))}
                    {listed.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 2 }}>No area matches.</Typography>
                    )}
                </List>
            </Drawer>
        </>
    );
}
